import React, { useState, useEffect, useCallback, useMemo } from "react";
import SimpleRangePicker from "./SimpleRangePicker";
import { Calendar } from "../shadcn/ui/calendar";
import { Button } from "../shadcn/ui/button";
import { Dialog, DialogContent, DialogTrigger } from "../shadcn/ui/dialog";
import { CalendarDays, X } from "lucide-react";
import { Badge } from "../shadcn/ui/badge";
import { Switch } from "../shadcn/ui/switch";
import { TooltipProvider } from "../shadcn/ui/tooltip";
import { cn } from "../lib/utils";
import { convertToUTC } from "../utils/timezoneUtils";

/**
 * SmartDatePicker - Unified date picker with seamless range/specific dates modes
 *
 * Automatically detects user's selection pattern and sends correct format to backend:
 * - Range Mode: Returns { startDate, endDate }
 * - Specific Dates Mode: Returns { specificDate: [date1, date2, ...] }
 *
 * User just sees a calendar - no complexity!
 */
const SmartDatePicker = ({
  value = null, // Can be { startDate, endDate } or { specificDate: [] }
  onChange,
  placeholder = "Select dates",
  className = "",
  disabled = false,
  existingBookings = [], // Array of existing bookings to highlight (pre-filtered by space)
  availableDates = new Set(), // Set of available dates to highlight in green
  instances = [], // Space instances for availability display
  instanceDayStatusFn = null, // Function to get instance status for a date
  maxSelectableDate = null, // Optional max date constraint (inclusive)
  minDate = null, // Optional min date constraint (inclusive)
}) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("range"); // "range" or "specific"
  const [selectedDates, setSelectedDates] = useState([]);
  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);
  const [hoveredDate, setHoveredDate] = useState(null);
  const ignoreValueSync = React.useRef(false);
  const startOfToday = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }, []);

  const effectiveMinDate = useMemo(() => {
    if (minDate) {
      const d = new Date(minDate);
      d.setHours(0, 0, 0, 0);
      return d;
    }
    return startOfToday;
  }, [minDate, startOfToday]);

  // Get dates that have existing bookings
  const getBookedDates = useCallback(() => {
    if (!existingBookings || existingBookings.length === 0) return new Set();

    const bookedDates = new Set();
    existingBookings.forEach(booking => {
      if (!booking.startTime || !booking.endTime) return;

      const start = new Date(booking.startTime);
      const end = new Date(booking.endTime);
      const current = new Date(start);

      // Add all dates within the booking range
      while (current <= end) {
        bookedDates.add(current.toDateString());
        current.setDate(current.getDate() + 1);
      }
    });

    return bookedDates;
  }, [existingBookings]);

  // Get booking info for a specific date
  const getBookingsForDate = useCallback(
    date => {
      if (!existingBookings || existingBookings.length === 0) return [];

      return existingBookings.filter(booking => {
        if (!booking.startTime || !booking.endTime) return false;

        const bookingStart = new Date(booking.startTime);
        const bookingEnd = new Date(booking.endTime);
        const checkDate = new Date(date);

        // Check if booking overlaps with this date
        return bookingStart <= checkDate && bookingEnd >= checkDate;
      });
    },
    [existingBookings]
  );

  // Memoize booked dates set
  const bookedDatesSet = useMemo(() => getBookedDates(), [getBookedDates]);

  // Memoize modifiers to handle status and past/future distinction
  const modifiers = useMemo(() => {
    return {
      full: date => {
        if (instanceDayStatusFn && Array.isArray(instances) && instances.length > 0) {
          const statuses = instances.map(inst => instanceDayStatusFn(inst, date));
          return statuses.every(s => s?.label === "Fully booked");
        }
        return bookedDatesSet.has(date.toDateString()); // Binary fallback
      },
      partial: date => {
        if (instanceDayStatusFn && Array.isArray(instances) && instances.length > 0) {
          const statuses = instances.map(inst => instanceDayStatusFn(inst, date));
          const allFull = statuses.every(s => s?.label === "Fully booked");
          const anyBooked = statuses.some(s => s?.label && s.label !== "Available");
          return anyBooked && !allFull;
        }
        return false; // Binary fallback doesn't know about partial
      },
      past: date => date < effectiveMinDate,
      available: date => availableDates.has(date.toDateString()),
      hoverRange: date => {
        if (!rangeStart || rangeEnd || !hoveredDate) return false;
        const s = new Date(rangeStart).setHours(0, 0, 0, 0);
        const h = new Date(hoveredDate).setHours(0, 0, 0, 0);
        const c = new Date(date).setHours(0, 0, 0, 0);
        return c >= Math.min(s, h) && c <= Math.max(s, h);
      },
    };
  }, [
    bookedDatesSet,
    availableDates,
    instanceDayStatusFn,
    instances,
    effectiveMinDate,
    rangeStart,
    rangeEnd,
    hoveredDate,
  ]);

  // Memoize modifier styles with better category mapping
  const modifierStyles = useMemo(
    () => ({
      full: {
        backgroundColor: "#fee2e2",
        color: "#dc2626",
        fontWeight: "bold",
      },
      partial: {
        backgroundColor: "#ffedd5",
        color: "#d97706",
        fontWeight: "bold",
      },
      past: {
        opacity: 0.6,
      },
      available: {
        backgroundColor: "#dcfce7",
        color: "#166534",
        fontWeight: "bold",
      },
      hoverRange: {
        backgroundColor: "#dbeafe",
        color: "#1e40af",
        borderRadius: "0px",
      },
    }),
    []
  );

  // Initialize from value prop — skip if we triggered the change ourselves
  useEffect(() => {
    if (ignoreValueSync.current) {
      ignoreValueSync.current = false;
      return;
    }
    if (value) {
      if (value.specificDate && Array.isArray(value.specificDate)) {
        setMode("specific");
        setSelectedDates(value.specificDate.map(d => new Date(d)));
      } else if (value.startDate && value.endDate) {
        setMode("range");
        setRangeStart(new Date(value.startDate));
        setRangeEnd(new Date(value.endDate));
      }
    }
  }, [value]);

  // Format date for display
  const formatDate = date => {
    if (!date) return "";
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Get display text for button
  const getDisplayText = () => {
    if (mode === "range") {
      if (rangeStart && rangeEnd) {
        return `${formatDate(rangeStart)} - ${formatDate(rangeEnd)}`;
      } else if (rangeStart) {
        return `${formatDate(rangeStart)} - ...`;
      }
    } else {
      if (selectedDates.length > 0) {
        if (selectedDates.length === 1) {
          return formatDate(selectedDates[0]);
        } else if (selectedDates.length === 2) {
          return `${formatDate(selectedDates[0])}, ${formatDate(selectedDates[1])}`;
        } else {
          return `${selectedDates.length} dates selected`;
        }
      }
    }
    return placeholder;
  };

  // Handle range selection from react-day-picker
  const handleRangeSelect = range => {
    if (!range) {
      setRangeStart(null);
      setRangeEnd(null);
      setHoveredDate(null);
      onChange(null);
      return;
    }

    if (range.from && range.to) {
      // Both clicked -> range complete
      setRangeStart(range.from);
      setRangeEnd(range.to);
      setHoveredDate(null);
      onChange({
        startDate: convertToUTC(range.from),
        endDate: convertToUTC(range.to),
      });
    } else if (range.from) {
      // 1st click -> start date set, waiting for end date
      setRangeStart(range.from);
      setRangeEnd(null);
      setHoveredDate(null);
      onChange({
        startDate: convertToUTC(range.from),
        endDate: convertToUTC(range.from),
      });
    }
  };

  // Handle specific dates selection
  const handleSpecificDateSelect = date => {
    if (!date) return;

    const dateStr = date.toISOString().split("T")[0];
    const isAlreadySelected = selectedDates.some(d => d.toISOString().split("T")[0] === dateStr);

    let newDates;
    if (isAlreadySelected) {
      // Remove date
      newDates = selectedDates.filter(d => d.toISOString().split("T")[0] !== dateStr);
    } else {
      // Add date
      newDates = [...selectedDates, date].sort((a, b) => a - b);
    }

    setSelectedDates(newDates);

    // Auto-detect if dates form a consecutive range
    if (newDates.length >= 2 && areConsecutive(newDates)) {
      // Convert to range mode automatically
      setMode("range");
      setRangeStart(newDates[0]);
      setRangeEnd(newDates[newDates.length - 1]);
      // Use convertToUTC for proper timezone handling
      onChange({
        startDate: convertToUTC(newDates[0]),
        endDate: convertToUTC(newDates[newDates.length - 1]),
      });
    } else {
      // Keep as specific dates - use convertToUTC for proper timezone handling
      onChange({
        specificDate: newDates.map(d => convertToUTC(d)),
      });
    }
  };

  // Check if dates are consecutive
  const areConsecutive = dates => {
    if (dates.length < 2) return false;

    const sortedDates = [...dates].sort((a, b) => a - b);
    for (let i = 1; i < sortedDates.length; i++) {
      const diff = Math.floor((sortedDates[i] - sortedDates[i - 1]) / (1000 * 60 * 60 * 24));
      if (diff !== 1) return false;
    }
    return true;
  };

  // Clear selection
  const handleClear = () => {
    setSelectedDates([]);
    setRangeStart(null);
    setRangeEnd(null);
    setHoveredDate(null);
    onChange(null);
  };

  // Apply selection and close
  const handleApply = () => {
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            "pl-2 justify-start text-left font-normal text-gray-700 hover:text-gray-700 hover:bg-transparent border-0 shadow-none h-11 w-full flex items-center",
            !value && "text-muted-foreground",
            className
          )}
          disabled={disabled}
          onClick={() => setOpen(!open)}
        >
          <CalendarDays className="mr-2 h-4 w-4 flex-shrink-0" />
          <span className="truncate">{getDisplayText()}</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="w-[760px] max-w-[98vw] p-0 flex flex-col max-h-[90vh] rounded-2xl shadow-2xl border-gray-100 overflow-hidden [&>button:last-child]:hidden">
        <div className="p-4 sm:px-6 border-b border-gray-100 bg-white shrink-0">
          {/* Mode Toggle */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-800">Select Dates</span>
            <div className="flex items-center bg-gray-100 p-1 rounded-full">
              <button
                type="button"
                className={cn(
                  "text-xs font-medium px-4 py-1.5 rounded-full transition-all duration-200",
                  mode === "range" ? "bg-white shadow-sm text-gray-900" : "text-gray-500 hover:text-gray-700"
                )}
                onClick={() => {
                  setMode("range");
                  setSelectedDates([]);
                  setRangeStart(null);
                  setRangeEnd(null);
                  setHoveredDate(null);
                }}
              >
                Range
              </button>
              <button
                type="button"
                className={cn(
                  "text-xs font-medium px-4 py-1.5 rounded-full transition-all duration-200",
                  mode === "specific" ? "bg-white shadow-sm text-gray-900" : "text-gray-500 hover:text-gray-700"
                )}
                onClick={() => {
                  setMode("specific");
                  setRangeStart(null);
                  setRangeEnd(null);
                  setHoveredDate(null);
                }}
              >
                Specific
              </button>
            </div>
          </div>

          {/* Selected Dates Display for Specific Mode */}
          {mode === "specific" && selectedDates.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4 max-h-24 overflow-y-auto pb-1">
              {selectedDates.map((date, idx) => (
                <Badge
                  key={idx}
                  variant="secondary"
                  className="text-xs bg-blue-50 text-blue-700 border-blue-100 hover:bg-blue-100 px-3 py-1 rounded-full transition-colors"
                >
                  {formatDate(date)}
                  <X
                    className="ml-1.5 h-3 w-3 cursor-pointer opacity-70 hover:opacity-100 transition-opacity"
                    onClick={e => {
                      e.stopPropagation();
                      handleSpecificDateSelect(date);
                    }}
                  />
                </Badge>
              ))}
            </div>
          )}
        </div>

        {/* Calendar */}
        <div className="px-4 sm:px-6 pt-6 pb-4 overflow-y-auto bg-white">
          <TooltipProvider>
            {mode === "range" ? (
              <SimpleRangePicker
                rangeStart={rangeStart}
                rangeEnd={rangeEnd}
                onRangeChange={({ start, end }) => {
                  setRangeStart(start);
                  setRangeEnd(end || null);
                  // Only notify parent when BOTH dates are selected
                  if (start && end) {
                    ignoreValueSync.current = true;
                    onChange({ startDate: convertToUTC(start), endDate: convertToUTC(end) });
                  }
                }}
                minDate={effectiveMinDate}
                maxDate={maxSelectableDate || undefined}
              />
            ) : (
              <Calendar
                mode="multiple"
                selected={selectedDates}
                onSelect={dates => {
                  if (Array.isArray(dates)) {
                    setSelectedDates(dates);
                    // Use convertToUTC for proper timezone handling
                    onChange({
                      specificDate: dates.map(d => convertToUTC(d)),
                    });
                  }
                }}
                numberOfMonths={2}
                className="border-0 p-0"
                disabled={date => {
                  if (!date) return true;
                  if (date < effectiveMinDate) return true; // Disable past dates
                  if (maxSelectableDate && date > maxSelectableDate) return true;
                  if (disabled) return true;
                  return false;
                }}
                modifiers={modifiers}
                modifiersStyles={modifierStyles}
                components={{
                  DayContent: ({ date }) => {
                    const bookings = getBookingsForDate(date);
                    const isBooked = bookings.length > 0;
                    const hasInstanceStatus = instanceDayStatusFn && Array.isArray(instances) && instances.length > 0;
                    const instanceStatuses = hasInstanceStatus
                      ? instances.map(inst => ({
                          instance: inst,
                          status: instanceDayStatusFn(inst, date),
                        }))
                      : [];

                    const aggregateStatus = (() => {
                      if (!hasInstanceStatus) return { label: null, color: null };
                      const priority = { "Fully booked": 3, Unavailable: 3, Partial: 2, Available: 1, Unknown: 0 };
                      return instanceStatuses.reduce(
                        (best, { status }) => {
                          const label = status?.label || "Unknown";
                          const color = status?.color || "bg-gray-400";
                          const score = priority[label] ?? 0;
                          return score > best.score ? { label, color, score } : best;
                        },
                        { label: null, color: null, score: -1 }
                      );
                    })();

                    const dayNumber = (
                      <div className="w-full h-full flex flex-col items-center justify-center gap-0.5">
                        <span>{date.getDate()}</span>
                        {aggregateStatus?.color && (
                          <span className={`h-1.5 w-1.5 rounded-full ${aggregateStatus.color}`} />
                        )}
                      </div>
                    );

                    return dayNumber;
                  },
                }}
              />
            )}
          </TooltipProvider>
        </div>

        {/* Actions */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex justify-between shrink-0 items-center">
          <Button
            variant="ghost"
            size="sm"
            className="text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-full px-4"
            onClick={handleClear}
          >
            Clear selection
          </Button>
          <Button
            size="sm"
            className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6 shadow-md hover:shadow-lg transition-all"
            onClick={handleApply}
          >
            Apply Dates
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SmartDatePicker;
